import { COMPONENT_QUERY } from '../../../constants/api';
import {
  findWeekAndMonth,
  isNumber,
  previousWeekExtractor,
} from '../../../data/branchPerformanceData';
import api from '../../../services/api';
import { formatNumberUsingUnit } from '../../../utils/formatUtils';

// Opps Insights Constants
export const OppsInsightTexts = {
  headline: 'Opportunities Insights',
  branchDropDownLabel: 'Branch',
  summaryText: 'Summary',
  avgAppointText: 'Avg Appointment Lead Time (Rolling 5 Weeks) ',
  oppsByStatusText: 'Opportunities by Status (YTD)',
};

const defaultInsightTextFunction = (parameter) => `No change in ${parameter} since last 5 weeks`;

const insightsArraySteringValidation = [
  'canceled',
  'commercial',
  'construction',
  'service',
  'cancelrate',
];

export const YTD_KEY_INSIGHTS = 'opps_insights_summary_query';
export const LEADTIME_KEY_INSIGHTS = 'opps_insights_lead_time_query';
export const BEFORE_FIVE_WEEKS_KEY_INSIGHTS = 'opps_insights_YTD_five_weeks_before_query';
export const CURRENT_WEEK_YTD_INSIGHTS = 'opps_insights_YTD_current_week_query';

// oppsInsights Functions

export const oppsTableOneHeaders = (transformedDataWithDate) => {
  const currentWeek = previousWeekExtractor(transformedDataWithDate)?.subtractedWeekValue; // updated as base logic upgraded in function itself to handle previous week and tuesday
  const currentMonth = findWeekAndMonth(transformedDataWithDate)?.fiscal_month;
  let headerArray = [
    { Header: '', accessor: '_Heading' },
    { Header: `YTD through FW${currentWeek}`, accessor: '_Month_Week' },
    { Header: '% vs Region', accessor: '_Region' },
    { Header: '% vs GALE Markets', accessor: '_Gale_Markets' },
    {
      Header: 'Insights',
      accessor: '_Insights',
      Cell: ({ cell }) => {
        return insightsPerCalculator(
          cell?.value?.value1,
          cell?.value?.value2,
          cell?.value?.parameter,
          cell?.value?.validationString,
          currentWeek
        );
      },
    },
  ];
  return headerArray?.length > 0 ? headerArray : [];
};

function isNullOrUndefinedOrEmpty(value) {
  return value === null || value === undefined || value === '' || value === 0;
}

const insightsPerCalculator = (value1, value2, parameter, validationString, currentWeek) => {
  // down good green display 3rd table insights -> 'canceled' || 'commercial' || 'construction' || 'service' if up red
  if (value1 === value2) return defaultInsightTextFunction(parameter);
  if (isNullOrUndefinedOrEmpty(value1) || isNullOrUndefinedOrEmpty(value2))
    return defaultInsightTextFunction(parameter);

  const calculatedPercent = isNumber(value1) && isNumber(value2) && (value1 / value2) * 100;
  const perCalc =
    isNumber(calculatedPercent) && calculatedPercent === 0
      ? calculatedPercent
      : isNumber(calculatedPercent) && calculatedPercent !== 0
        ? calculatedPercent - 100
        : 'N/A';

  if (insightsArraySteringValidation?.includes(validationString)) {
    return isNumber(perCalc) && perCalc > 0 ? (
      <div>
        {parameter}{' '}
        {currentWeek > 5 ? (
          <span style={{ color: 'red', fontWeight: 700 }}>up {perCalc?.toFixed(2)}% </span>
        ) : (
          '-'
        )}{' '}
        since last 5 weeks
      </div>
    ) : perCalc < 0 ? (
      <div>
        {parameter}{' '}
        {currentWeek > 5 ? (
          <span style={{ color: 'green', fontWeight: 700 }}>down {(perCalc * -1).toFixed(2)}%</span>
        ) : (
          '-'
        )}{' '}
        since last 5 weeks
      </div>
    ) : (
      defaultInsightTextFunction(parameter)
    );
  } else if (['avgjobsize', 'closerate'].includes(validationString)) {
    return isNumber(perCalc) && perCalc > 0 ? (
      <div>
        {parameter}{' '}
        {currentWeek > 5 ? (
          <span style={{ color: 'green', fontWeight: 700 }}>up {perCalc?.toFixed(2)}%</span>
        ) : (
          '-'
        )}{' '}
        since last 5 weeks
      </div>
    ) : perCalc < 0 ? (
      <div>
        {parameter}{' '}
        {currentWeek > 5 ? (
          <span style={{ color: 'red', fontWeight: 700 }}>down {(perCalc * -1).toFixed(2)}%</span>
        ) : (
          '-'
        )}{' '}
        since last 5 weeks
      </div>
    ) : (
      defaultInsightTextFunction(parameter)
    );
  }
};

export const OppsTableOneRows = (insightSummaryData, transformedDataWithDate) => {
  const currentWeekObj = previousWeekExtractor(transformedDataWithDate); // updated as base logic upgraded in function itself to handle previous week and tuesday
  // fiveWeeksBefore data is to be used for the insights percentage calculation
  const fiveWeekBeforeValueObj = previousWeekExtractor(transformedDataWithDate, 5); // updated as base logic upgraded in function itself to handle previous week and tuesday
  const fiveWeekBeforeData =
    insightSummaryData?.opps_insights_summary_query?.result?.length > 0
      ? insightSummaryData?.opps_insights_summary_query?.result?.filter(
          (data) =>
            data?.fiscal_week === fiveWeekBeforeValueObj?.subtractedWeekValue &&
            data?.fiscal_year === fiveWeekBeforeValueObj?.currentYear
        )?.[0]
      : {};
  const currentWeekData =
    insightSummaryData?.opps_insights_summary_query?.result?.length > 0
      ? insightSummaryData?.opps_insights_summary_query?.result?.filter(
          (data) =>
            data?.fiscal_week === currentWeekObj?.subtractedWeekValue &&
            data?.fiscal_year === currentWeekObj?.currentYear
        )?.[0]
      : {};
  let rowData = [
    {
      _Heading: 'Close Rate',
      _Month_Week: formatNumberUsingUnit(currentWeekData?.close_rate?.toFixed(2), '%'),
      _Region: formatNumberUsingUnit(currentWeekData?.close_rate_comp_to_region?.toFixed(2), '%'),
      _Gale_Markets: formatNumberUsingUnit(
        currentWeekData?.close_rate_comp_to_gale_avg?.toFixed(2),
        '%'
      ),
      _Insights: {
        value1: currentWeekData?.close_rate,
        value2: fiveWeekBeforeData?.close_rate,
        parameter: 'Close Rate',
        validationString: 'closerate',
      },
    },
    {
      _Heading: 'Avg. Job Size',
      _Month_Week: formatNumberUsingUnit(currentWeekData?.avg_job_size, '$'),
      _Region: formatNumberUsingUnit(currentWeekData?.avg_job_size_comp_to_region?.toFixed(2), '%'),
      _Gale_Markets: formatNumberUsingUnit(
        currentWeekData?.avg_job_size_comp_to_gale_avg?.toFixed(2),
        '%'
      ),
      _Insights: {
        value1: currentWeekData?.avg_job_size_week,
        value2: fiveWeekBeforeData?.avg_job_size_week,
        parameter: 'Avg. Job Size',
        validationString: 'avgjobsize',
      },
    },
    {
      _Heading: 'Cancel/ Transfer Rate',
      _Month_Week: formatNumberUsingUnit(currentWeekData?.cancel_or_transfer_rate?.toFixed(2), '%'),
      _Region: formatNumberUsingUnit(
        currentWeekData?.cancel_transfer_rate_comp_to_region?.toFixed(2),
        '%'
      ),
      _Gale_Markets: formatNumberUsingUnit(
        currentWeekData?.cancel_transfer_rate_comp_to_gale_avg,
        '%'
      ),
      _Insights: {
        value1: currentWeekData?.cancel_or_transfer_rate_week,
        value2: fiveWeekBeforeData?.cancel_or_transfer_rate_week,
        parameter: 'Cancel/ Transfer Rate',
        validationString: 'cancelrate',
      },
    },
  ];
  return rowData?.length > 0 ? rowData : [];
};

export const oppsTableTwoHeaders = (transformedDataWithDate) => {
  let tableTwoHeaders = [];
  for (let i = 4; i >= 0; i--) {
    tableTwoHeaders?.push({
      Header: `W${previousWeekExtractor(transformedDataWithDate, i)?.subtractedWeekValue}`,
      accessor: `_W${previousWeekExtractor(transformedDataWithDate, i)?.subtractedWeekValue}`,
    });
  }
  tableTwoHeaders.push({ Header: 'Avg', accessor: '_Avg' });
  tableTwoHeaders?.splice(0, 0, {
    Header: () => <span style={{ width: '80px', display: 'inline-block' }}></span>,
    accessor: '_Heading',
  });
  return tableTwoHeaders?.length > 0 ? tableTwoHeaders : [];
};

export const OppsTableTwoRows = (oppsLeadTimeData, transformedDataWithDate) => {
  let tableTwoRowsData = [];
  const leadTimeData = oppsLeadTimeData?.opps_insights_lead_time_query?.result;
  for (let i = 4; i >= 0; i--) {
    tableTwoRowsData?.push(
      `_W${previousWeekExtractor(transformedDataWithDate, i)?.subtractedWeekValue}`
    );
  }
  const updatedTableTwoRowsData = tableTwoRowsData?.reduce((acc, key, index) => {
    acc[key] =
      leadTimeData
        ?.filter((data) => data?.fiscal_week === parseInt(key.slice(2)))[0]
        ?.avg_lead_time.toFixed(2) || 'N/A';
    return acc;
  }, {});
  // in case of "N/A" or no data for that particular week
  const avgOfWeeks =
    Object.values(updatedTableTwoRowsData)
      .filter((value) => value !== 'N/A') // Exclude 'N/A' values
      .reduce((acc, value) => acc + parseFloat(value), 0) /
    Object.values(updatedTableTwoRowsData).filter((value) => value !== 'N/A').length; // Adjust divisor based on non-'N/A' values
  const avgAndHeadingAddition = {
    _Heading: 'Average of ALT',
    ...updatedTableTwoRowsData,
    _Avg: avgOfWeeks.toFixed(2) || 'N/A',
  };
  return [avgAndHeadingAddition];
};

export const oppsTableThreeHeaders = (transformedDataWithDate) => {
  const currentWeek = previousWeekExtractor(transformedDataWithDate)?.subtractedWeekValue; // updated as base logic upgraded in function itself to handle previous week and tuesday
  let headerArray = [
    { Header: '', accessor: '_Heading' },
    { Header: 'Count', accessor: '_Count' },
    { Header: '% in Status', accessor: '_Status' },
    {
      Header: 'Insights',
      accessor: '_Insights',
      Cell: ({ cell }) => {
        return insightsPerCalculator(
          cell?.value?.value1,
          cell?.value?.value2,
          cell?.value?.parameter,
          cell?.value?.validationString,
          currentWeek
        );
      },
    },
  ];
  return headerArray;
};

export const OppsTableThreeRows = (oppsStatusData, oppsStatusYtdData) => {
  const fiveWeeksBeforeYTDData =
    oppsStatusData?.opps_insights_YTD_five_weeks_before_query?.result?.length > 0 &&
    oppsStatusData?.opps_insights_YTD_five_weeks_before_query?.result[0];
  const YTDData =
    oppsStatusYtdData?.opps_insights_YTD_current_week_query?.result?.length > 0 &&
    oppsStatusYtdData?.opps_insights_YTD_current_week_query?.result[0];
  let rowData = [
    {
      _Heading: 'Canceled',
      _Count: YTDData?.cancelled_opps || 0,
      _Status: isNumber(YTDData?.cancelled_status)
        ? YTDData?.cancelled_status?.toFixed(2) + '%'
        : 'N/A',
      _Insights: {
        value1: YTDData?.cancelled_opps,
        value2: fiveWeeksBeforeYTDData?.cancelled_opps,
        parameter: 'Canceled',
        validationString: 'canceled',
      },
    },
    {
      _Heading: 'Transferred to Commercial',
      _Count: YTDData?.transfer_to_commercial || 0,
      _Status: isNumber(YTDData?.transfer_to_commercial_status)
        ? YTDData?.transfer_to_commercial_status?.toFixed(2) + '%'
        : 'N/A',
      _Insights: {
        value1: YTDData?.transfer_to_commercial,
        value2: fiveWeeksBeforeYTDData?.transfer_to_commercial,
        parameter: 'Transferred to Commercial',
        validationString: 'commercial',
      },
    },
    {
      _Heading: 'Transferred to New Construction',
      _Count: YTDData?.transfer_to_new_costruction || 0,
      _Status: isNumber(YTDData?.transfer_to_new_costruction_status)
        ? YTDData?.transfer_to_new_costruction_status?.toFixed(2) + '%'
        : 'N/A',
      _Insights: {
        value1: YTDData?.transfer_to_new_costruction,
        value2: fiveWeeksBeforeYTDData?.transfer_to_new_costruction,
        parameter: 'Transferred to New Construction',
        validationString: 'construction',
      },
    },
    {
      _Heading: 'Transferred to Service',
      _Count: YTDData?.transfer_to_service || 0,
      _Status: isNumber(YTDData?.transfer_to_service_status)
        ? YTDData?.transfer_to_service_status?.toFixed(2) + '%'
        : 'N/A',
      _Insights: {
        value1: YTDData?.transfer_to_service,
        value2: fiveWeeksBeforeYTDData?.transfer_to_service,
        parameter: 'Transferred to Service',
        validationString: 'service',
      },
    },
  ];
  return rowData?.length > 0 ? rowData : [];
};

// services functions

const getPayload = (key, storeID, filterOne, filterTwo) => {
  switch (key) {
    case YTD_KEY_INSIGHTS:
      return {
        [key]: {
          filters: [
            { col: 'store_id', op: '==', val: storeID },
            { col: 'dateindex', op: 'IN', val: [filterOne, filterTwo] },
          ],
        },
      };

    case LEADTIME_KEY_INSIGHTS:
      return {
        [key]: {
          filters: [
            { col: 'store_id', op: '==', val: storeID },
            { col: 'dateindex', op: '>=', val: filterOne },
            { col: 'dateindex', op: '<=', val: filterTwo },
          ],
        },
      };

    case BEFORE_FIVE_WEEKS_KEY_INSIGHTS:
      return {
        [key]: {
          filters: [
            { col: 'store_id', op: '==', val: storeID },
            { col: 'dateindex', op: 'IN', val: [filterOne] },
          ],
        },
      };

    case CURRENT_WEEK_YTD_INSIGHTS:
      return {
        [key]: {
          filters: [
            { col: 'store_id', op: '==', val: storeID },
            { col: 'dateindex', op: 'IN', val: [filterOne] },
          ],
        },
      };

    default:
      return null;
  }
};

// opps insights services

export async function fetchInsightsSummary(data) {
  try {
    const response = await api.post(
      COMPONENT_QUERY + `?component=${data?.YTD_KEY_INSIGHTS}`,
      getPayload(
        data?.YTD_KEY_INSIGHTS,
        data?.storeId,
        data?.tableOneCurrentWeek,
        data?.tableOneFilter
      )
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
}

export async function fetchLeadTime(data) {
  try {
    const response = await api.post(
      COMPONENT_QUERY + `?component=${data?.LEADTIME_KEY_INSIGHTS}`,
      getPayload(
        data?.LEADTIME_KEY_INSIGHTS,
        data?.storeId,
        data?.tableTwoFilterOne,
        data?.tableTwoFilterTwo
      )
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
}

export async function fetchOppsStatus(data) {
  try {
    const response = await api.post(
      COMPONENT_QUERY + `?component=${data?.BEFORE_FIVE_WEEKS_KEY_INSIGHTS}`,
      getPayload(data?.BEFORE_FIVE_WEEKS_KEY_INSIGHTS, data?.storeId, data?.tableThreeUpdatedFilter)
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
}

export async function fetchOppsYTD(data) {
  try {
    const response = await api.post(
      COMPONENT_QUERY + `?component=${data?.CURRENT_WEEK_YTD_INSIGHTS}`,
      getPayload(data?.CURRENT_WEEK_YTD_INSIGHTS, data?.storeId, data?.tableTwoFilterTwo)
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
}
